import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { FeatureCollection } from 'geojson'
import { SpeedProfileProperties } from '../models/speed-profile.model'

@Injectable()
export class DataSpeedProfileService {
  private speedProfiles: FeatureCollection<GeoJSON.LineString, SpeedProfileProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  public isDataInitialized = false

  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadSpeedProfilesData(
    isHistoryEnabled: boolean,
    bounds: maplibregl.LngLatBounds,
    timestamp?: Date,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()

    const speedProfiles =
      isHistoryEnabled && timestamp
        ? await this.loadSpeedProfilesForHistory(timestamp, bounds)
        : await this.loadSpeedProfiles(bounds)

    this.isDataInitialized = true
    this.speedProfiles.features = speedProfiles.features

    return this
  }

  async loadSpeedProfiles(
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.LineString, SpeedProfileProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(`${this.config.backend_url}vzo/speed-profiles`)
    url.searchParams.set('minLng', bounds.getWest().toString())
    url.searchParams.set('minLat', bounds.getSouth().toString())
    url.searchParams.set('maxLng', bounds.getEast().toString())
    url.searchParams.set('maxLat', bounds.getNorth().toString())

    try {
      const result = await firstValueFrom(
        this.http
          .get<FeatureCollection<GeoJSON.LineString, SpeedProfileProperties>>(
            url.toString(),
            httpOptions,
          )
          .pipe(takeUntil(this.cancelPreviousRequest$)),
        { defaultValue: { type: 'FeatureCollection' as const, features: [] } },
      )
      return result
    } catch (error) {
      console.error('Error loading speed profile data:', error)
      return {
        type: 'FeatureCollection',
        features: [],
      }
    }
  }

  async loadSpeedProfilesForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.LineString, SpeedProfileProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }
    const url = new URL(`${this.config.backend_url}vzo/speed-profiles/historic`)

    try {
      const result = await firstValueFrom(
        this.http
          .get<FeatureCollection<GeoJSON.LineString, SpeedProfileProperties>>(
            url.toString(),
            httpOptions,
          )
          .pipe(takeUntil(this.cancelPreviousRequest$)),
        { defaultValue: { type: 'FeatureCollection' as const, features: [] } },
      )
      return result
    } catch (error) {
      console.error('Error loading speed profile history data:', error)
      return {
        type: 'FeatureCollection',
        features: [],
      }
    }
  }

  getSpeedProfiles(): FeatureCollection<GeoJSON.LineString, SpeedProfileProperties> {
    return this.speedProfiles
  }

  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
